const SIZES = {
  xs: { d: 14, t: 2 },
  sm: { d: 16, t: 2.5 },
  md: { d: 24, t: 3 },
  lg: { d: 32, t: 3.5 },
  xl: { d: 48, t: 4 },
};

/**
 * Spinner — gradient-tail ring spinner (conic-gradient + animate-spin).
 *
 * Props:
 *   size      — "xs" | "sm" | "md" | "lg" | "xl"  (default "md")
 *   color     — Tailwind text-color class           (default "text-primary-500")
 *   className — extra classes
 */
export default function Spinner({ size = "md", color = "text-primary-500", className = "" }) {
  const { d, t } = SIZES[size] || SIZES.md;

  return (
    <span
      className={`inline-block animate-spin ${color} ${className}`}
      role="status"
      aria-label="Loading"
      style={{
        width: d,
        height: d,
        minWidth: d,
        minHeight: d,
        borderRadius: "50%",
        background: "conic-gradient(from 0deg, transparent 30%, currentColor)",
        WebkitMask: `radial-gradient(farthest-side, transparent calc(100% - ${t}px), #000 calc(100% - ${t}px + 0.5px))`,
        mask: `radial-gradient(farthest-side, transparent calc(100% - ${t}px), #000 calc(100% - ${t}px + 0.5px))`,
      }}
    />
  );
}

/**
 * PageLoader — centered spinner with soft glow + label.
 */
export function PageLoader({ label = "Loading…", className = "" }) {
  return (
    <div className={`flex flex-col items-center justify-center gap-4 py-16 ${className}`}>
      <div className="relative flex items-center justify-center">
        <span className="absolute w-14 h-14 rounded-full bg-primary-400/15 blur-xl" />
        <Spinner size="xl" />
      </div>
      {label && (
        <p className="text-sm text-gray-400 font-medium tracking-wide select-none animate-pulse">
          {label}
        </p>
      )}
    </div>
  );
}

/**
 * InlineLoader — compact horizontal spinner + label for buttons / rows.
 */
export function InlineLoader({ label = "Loading…", size = "sm", color = "text-primary-500" }) {
  return (
    <span className="inline-flex items-center gap-2">
      <Spinner size={size} color={color} />
      {label && <span className="text-sm text-gray-500">{label}</span>}
    </span>
  );
}
